import React, { useEffect, useState } from "react";
import { Modal, Button, Tag, Input, message, Select, Table } from "antd";
import { ThunderboltOutlined, EyeOutlined, CheckOutlined } from "@ant-design/icons";

import {
  getSettlementsApi,
  getSettlementItemsApi,
  generateSettlementsApi,
  generateVendorSettlementApi,
  markSettlementPaidApi,
  getVendorsApi,
} from "../../services/api";
import AppTable from "../../common/AppTable";

const ACCENT = "#534ab7";
const STATUS_FILTERS = ["All", "Pending", "Paid"];
const STATUS_COLORS = { PENDING: "gold", PAID: "success" };

function inr(v) {
  return `₹${(v ?? 0).toLocaleString("en-IN")}`;
}

function maskAccountNumber(num) {
  if (!num) return "—";
  const s = String(num);
  return s.length <= 4 ? s : "•".repeat(s.length - 4) + s.slice(-4);
}

export default function Settlements() {
  const [settlements, setSettlements] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [statusFilter, setStatusFilter] = useState("All");
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [genVendorId, setGenVendorId] = useState(null);

  const [itemsTarget, setItemsTarget] = useState(null);
  const [items, setItems] = useState([]);
  const [itemsLoading, setItemsLoading] = useState(false);

  const [payTarget, setPayTarget] = useState(null);
  const [paymentReference, setPaymentReference] = useState("");

  const fetchSettlements = async (filter = statusFilter) => {
    setLoading(true);
    try {
      const params = filter === "All" ? {} : { status: filter.toUpperCase() };
      const res = await getSettlementsApi(params);
      setSettlements((res.data.data || []).slice().sort((a, b) => b.id - a.id));
    } catch {
      message.error("Failed to load settlements");
    } finally {
      setLoading(false);
    }
  };

  const fetchVendors = async () => {
    try {
      const res = await getVendorsApi();
      setVendors(res.data.data || []);
    } catch {
      // Non-fatal — the per-vendor generate dropdown just stays empty.
    }
  };

  useEffect(() => {
    fetchSettlements();
    fetchVendors();
  }, []);

  const handleGenerateAll = async () => {
    setGenerating(true);
    try {
      const res = await generateSettlementsApi();
      const { created = [], skipped = [] } = res.data.data || {};

      if (created.length > 0) {
        message.success(`Generated ${created.length} settlement${created.length > 1 ? "s" : ""}`);
      } else if (skipped.length === 0) {
        message.info("No vendors had settleable sales right now");
      }

      if (skipped.length > 0) {
        Modal.warning({
          title: `${skipped.length} vendor${skipped.length > 1 ? "s" : ""} skipped`,
          content: (
            <div>
              {skipped.map((s) => (
                <div key={s.vendorId} style={{ marginBottom: 6 }}>
                  <strong>{s.vendorName}</strong> — {s.reason}
                </div>
              ))}
            </div>
          ),
        });
      }

      fetchSettlements();
    } catch (err) {
      message.error(err?.response?.data?.data || "Failed to generate settlements");
    } finally {
      setGenerating(false);
    }
  };

  const handleGenerateForVendor = async () => {
    if (!genVendorId) return;
    setGenerating(true);
    try {
      await generateVendorSettlementApi(genVendorId);
      message.success("Settlement generated");
      setGenVendorId(null);
      fetchSettlements();
    } catch (err) {
      message.error(err?.response?.data?.data || "Failed to generate settlement");
    } finally {
      setGenerating(false);
    }
  };

  const openItems = async (record) => {
    setItemsTarget(record);
    setItemsLoading(true);
    try {
      const res = await getSettlementItemsApi(record.id);
      setItems(res.data.data || []);
    } catch {
      message.error("Failed to load settlement items");
    } finally {
      setItemsLoading(false);
    }
  };

  const openMarkPaid = (record) => {
    setPayTarget(record);
    setPaymentReference("");
  };

  const handleMarkPaid = async () => {
    try {
      await markSettlementPaidApi(payTarget.id, paymentReference);
      message.success("Settlement marked as paid");
      setPayTarget(null);
      fetchSettlements();
    } catch (err) {
      message.error(err?.response?.data?.data || "Failed to mark settlement as paid");
    }
  };

  const columns = [
    {
      title: "#",
      dataIndex: "id",
      width: 60,
      render: (v) => <span style={{ color: "#aaa", fontSize: 12 }}>#{v}</span>,
    },
    {
      title: "Vendor",
      dataIndex: "vendor",
      render: (v) => v?.name || "-",
    },
    {
      title: "Period",
      render: (_, r) => (
        <span style={{ fontSize: 12.5 }}>
          {r.periodStart} → {r.periodEnd}
        </span>
      ),
    },
    {
      title: "Gross Sales",
      dataIndex: "grossSalesAmount",
      render: inr,
    },
    {
      title: "Commission (+GST)",
      render: (_, r) => (
        <span style={{ fontSize: 12.5 }}>
          {inr(r.totalCommissionAmount)} + {inr(r.totalCommissionGstAmount)}
        </span>
      ),
    },
    {
      title: "TCS",
      dataIndex: "totalTcsAmount",
      render: inr,
    },
    {
      title: "Net Payable",
      dataIndex: "netPayableAmount",
      render: (v) => <span style={{ fontWeight: 700, color: "#2e7d32" }}>{inr(v)}</span>,
    },
    {
      title: "Payout Account",
      render: (_, r) => (
        <span style={{ fontSize: 12.5 }}>
          {maskAccountNumber(r.bankAccountNumber)}
          {r.bankIfsc ? ` · ${r.bankIfsc}` : ""}
        </span>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (v) => <Tag color={STATUS_COLORS[v] || "default"}>{v}</Tag>,
    },
    {
      title: "Actions",
      width: 90,
      align: "center",
      render: (_, r) => (
        <div style={{ display: "flex", gap: 6, justifyContent: "center" }}>
          <Button icon={<EyeOutlined />} size="small" onClick={() => openItems(r)} />
          {r.status === "PENDING" && (
            <Button icon={<CheckOutlined />} size="small" type="primary" style={{ background: "#2e7d32", border: "none" }} onClick={() => openMarkPaid(r)} />
          )}
        </div>
      ),
    },
  ];

  const itemColumns = [
    { title: "Product", dataIndex: "productName" },
    { title: "Qty", dataIndex: "quantity", width: 60, align: "center" },
    { title: "Gross", dataIndex: "grossAmount", render: inr },
    { title: "GST Rate", dataIndex: "gstRate", width: 90, align: "center", render: (v) => `${v}%` },
    { title: "Commission", dataIndex: "commissionAmount", render: inr },
    { title: "Comm. GST", dataIndex: "commissionGstAmount", render: inr },
    { title: "TCS", dataIndex: "tcsAmount", render: inr },
    { title: "Net", dataIndex: "netAmount", render: (v) => <strong>{inr(v)}</strong> },
  ];

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">Settlements</div>
          <div className="page-sub">
            {settlements.length} settlements — money collected via Shiprocket/Razorpay lands here first; this is what each vendor is actually owed
          </div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <Select
            allowClear
            placeholder="Generate for one vendor…"
            style={{ width: 220 }}
            value={genVendorId}
            onChange={setGenVendorId}
            options={vendors.map((v) => ({ label: v.name, value: v.id }))}
            showSearch
            filterOption={(input, option) => (option?.label ?? "").toLowerCase().includes(input.toLowerCase())}
          />
          <Button onClick={handleGenerateForVendor} loading={generating} disabled={!genVendorId}>
            Generate
          </Button>
          <Button
            type="primary"
            icon={<ThunderboltOutlined />}
            style={{ background: ACCENT, border: "none", borderRadius: 8 }}
            loading={generating}
            onClick={handleGenerateAll}
          >
            Generate All Due
          </Button>
        </div>
      </div>

      <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
        {STATUS_FILTERS.map((f) => (
          <button
            key={f}
            className={`filter-btn${statusFilter === f ? " active" : ""}`}
            onClick={() => { setStatusFilter(f); fetchSettlements(f); }}
          >
            {f}
          </button>
        ))}
      </div>

      <AppTable columns={columns} data={settlements} loading={loading} />

      <Modal
        title={itemsTarget ? `Settlement #${itemsTarget.id} — ${itemsTarget.vendor?.name}` : "Settlement Items"}
        open={!!itemsTarget}
        onCancel={() => setItemsTarget(null)}
        footer={[<Button key="close" onClick={() => setItemsTarget(null)}>Close</Button>]}
        width={780}
      >
        <Table
          columns={itemColumns}
          dataSource={items}
          rowKey="id"
          loading={itemsLoading}
          pagination={false}
          size="small"
          scroll={{ x: true }}
        />
      </Modal>

      <Modal
        title={payTarget ? `Mark Settlement #${payTarget.id} as Paid` : "Mark as Paid"}
        open={!!payTarget}
        onOk={handleMarkPaid}
        onCancel={() => setPayTarget(null)}
        okText="Mark Paid"
        okButtonProps={{ style: { background: "#2e7d32", border: "none" } }}
      >
        {payTarget && (
          <div style={{ marginBottom: 12, fontSize: 13, color: "#555" }}>
            Confirms {inr(payTarget.netPayableAmount)} was transferred to {payTarget.vendor?.name}'s account
            ({maskAccountNumber(payTarget.bankAccountNumber)}) outside this system.
          </div>
        )}
        <Input
          placeholder="Payment reference / UTR number"
          value={paymentReference}
          onChange={(e) => setPaymentReference(e.target.value)}
        />
      </Modal>
    </div>
  );
}
